import React, { useState, useEffect, useCallback } from 'react';
import {
  fetchShipmentDisputes,
  updateShipmentDisputes,
  deleteShipmentDispute,
} from '../../services/api';
import './disputesComponent.css';

const DisputesComponent = ({ shipmentNumber, onClose }) => {
  const [disputes, setDisputes] = useState([]);
  const [selectedDisputes, setSelectedDisputes] = useState({});

  const initializeSelectedDisputes = disputesData => {
    const initialDisputes = {};
    disputesData.forEach(dispute => {
      initialDisputes[dispute.dispute_type] = {
        id: dispute.id,
        delayType: dispute.delay_days !== null ? 'days' : 'hours',
        value: dispute.delay_days !== null ? dispute.delay_days : dispute.delay_hours || 0,
      };
    });
    setSelectedDisputes(initialDisputes);
  };

  const fetchDisputes = useCallback(async () => {
    try {
      const response = await fetchShipmentDisputes(shipmentNumber);
      setDisputes(response.results);
      initializeSelectedDisputes(response.results);
    } catch (error) {
      console.error(`Error fetching disputes for shipment ${shipmentNumber}`, error);
    }
  }, [shipmentNumber]);

  useEffect(() => {
    fetchDisputes();
  }, [fetchDisputes]);

  const handleDisputeTypeChange = disputeType => {
    setSelectedDisputes(prevState => {
      const newState = { ...prevState };
      if (newState[disputeType]) {
        delete newState[disputeType];
      } else {
        newState[disputeType] = {
          delayType: 'hours',
          value: 0,
        };
      }
      return newState;
    });
  };

  const handleDelayTypeChange = (disputeType, delayType) => {
    setSelectedDisputes(prevState => ({
      ...prevState,
      [disputeType]: {
        ...prevState[disputeType],
        delayType,
      },
    }));
  };

  const handleValueChange = (disputeType, value) => {
    setSelectedDisputes(prevState => ({
      ...prevState,
      [disputeType]: {
        ...prevState[disputeType],
        value,
      },
    }));
  };

  const handleSubmit = async () => {
    try {
      const disputesData = Object.keys(selectedDisputes).map(disputeType => {
        const dispute = selectedDisputes[disputeType];

        return {
          dispute_type: disputeType,
          delay_days:
            (disputeType === 'retard_enlevement' || disputeType === 'retard_livraison') &&
            dispute.delayType === 'days'
              ? dispute.value
              : null,
          delay_hours:
            (disputeType === 'retard_enlevement' || disputeType === 'retard_livraison') &&
            dispute.delayType === 'hours'
              ? dispute.value
              : null,
        };
      });

      await updateShipmentDisputes(shipmentNumber, disputesData);

      for (const initialDispute of disputes) {
        if (!selectedDisputes[initialDispute.dispute_type]) {
          await deleteShipmentDispute(shipmentNumber, initialDispute.id);
        }
      }

      fetchDisputes();
      onClose(disputesData.length > 0);
    } catch (error) {
      console.error('Error submitting disputes', error);
    }
  };

  return (
    <div className="overlay">
      <div className="shipment-disputes-content">
        <h2>Litiges dossier {shipmentNumber}</h2>
        <div className="shipment-disputes-form">
          <div className="shipment-disputes-type-container">
            <label className="shipment-disputes-type-title">
              <input
                type="checkbox"
                checked={!!selectedDisputes['retard_enlevement']}
                onChange={() => handleDisputeTypeChange('retard_enlevement')}
              />
              Retard Enlèvement
            </label>
            {selectedDisputes['retard_enlevement'] && (
              <>
                <label>
                  <input
                    type="radio"
                    value="days"
                    checked={selectedDisputes['retard_enlevement'].delayType === 'days'}
                    onChange={() => handleDelayTypeChange('retard_enlevement', 'days')}
                  />
                  Jours de retard
                </label>
                <label>
                  <input
                    type="radio"
                    value="hours"
                    checked={selectedDisputes['retard_enlevement'].delayType === 'hours'}
                    onChange={() => handleDelayTypeChange('retard_enlevement', 'hours')}
                  />
                  Tranche horaire
                </label>
                <input
                  type="number"
                  value={selectedDisputes['retard_enlevement'].value}
                  onChange={e => handleValueChange('retard_enlevement', e.target.value)}
                />
                <p>
                  {selectedDisputes['retard_enlevement'].delayType === 'hours' ? 'heures' : 'jours'}
                </p>
              </>
            )}
          </div>

          <div className="shipment-disputes-type-container">
            <label className="shipment-disputes-type-title">
              <input
                type="checkbox"
                checked={!!selectedDisputes['retard_livraison']}
                onChange={() => handleDisputeTypeChange('retard_livraison')}
              />
              Retard Livraison
            </label>
            {selectedDisputes['retard_livraison'] && (
              <>
                <label>
                  <input
                    type="radio"
                    value="days"
                    checked={selectedDisputes['retard_livraison'].delayType === 'days'}
                    onChange={() => handleDelayTypeChange('retard_livraison', 'days')}
                  />
                  Jours de retard
                </label>
                <label>
                  <input
                    type="radio"
                    value="hours"
                    checked={selectedDisputes['retard_livraison'].delayType === 'hours'}
                    onChange={() => handleDelayTypeChange('retard_livraison', 'hours')}
                  />
                  Tranche horaire
                </label>
                <input
                  type="number"
                  value={selectedDisputes['retard_livraison'].value}
                  onChange={e => handleValueChange('retard_livraison', e.target.value)}
                />
                <p>
                  {selectedDisputes['retard_livraison'].delayType === 'hours' ? 'heures' : 'jours'}
                </p>
              </>
            )}
          </div>

          <div className="shipment-disputes-type-container">
            <label className="shipment-disputes-type-title">
              <input
                type="checkbox"
                checked={!!selectedDisputes['casse']}
                onChange={() => handleDisputeTypeChange('casse')}
              />
              Casse
            </label>
          </div>
          <div className="shipment-disputes-type-container">
            <label className="shipment-disputes-type-title">
              <input
                type="checkbox"
                checked={!!selectedDisputes['manquant']}
                onChange={() => handleDisputeTypeChange('manquant')}
              />
              Manquant
            </label>
          </div>
          <div className="shipment-disputes-type-container">
            <label className="shipment-disputes-type-title">
              <input
                type="checkbox"
                checked={!!selectedDisputes['perte']}
                onChange={() => handleDisputeTypeChange('perte')}
              />
              Perte
            </label>
          </div>
        </div>
        <div className="shipment-disputes-endbuttons-container">
          <button onClick={handleSubmit}>Valider</button>
          <button onClick={() => onClose(selectedDisputes.length > 0)}>Annuler</button>
        </div>
      </div>
    </div>
  );
};

export default DisputesComponent;
